import { useCallback, useEffect, useState } from 'react'
import { httpsCallable } from 'firebase/functions'
import SignIn from '../components/SignIn'
import BuyButton from '../components/BuyButton'
import { errorMessage, useAuth } from '../lib/useAuth'
import { downloadURL, isFirebaseConfigured, requireFunctions } from '../lib/firebase'

interface Activation {
  machineId: string
  machineName: string
  activatedAt: number
  lastSeenAt: number
}

interface License {
  key: string
  seats: number
  createdAt: number | null
  activations: Activation[]
}

interface MachinesProps {
  licenseKey: string
  activations: Activation[]
  seats: number
  onChanged: () => void
}

function Machines({ licenseKey, activations, seats, onChanged }: MachinesProps) {
  const [busy, setBusy] = useState<string | null>(null)

  async function deactivate(machineId: string) {
    setBusy(machineId)
    try {
      await fetch('/api/license/deactivate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: licenseKey, machineId }),
      })
      onChanged()
    } finally {
      setBusy(null)
    }
  }

  return (
    <dl className="rows">
      {activations.length === 0 && (
        <div className="row"><dt>No Mac activated yet</dt><dd /></div>
      )}
      {activations.map((a) => (
        <div className="row" key={a.machineId}>
          <dt>{a.machineName}</dt>
          <dd>
            <button className="btn btn-ghost" style={{ padding: '.3rem .7rem', fontSize: '.85rem' }}
              disabled={busy === a.machineId} onClick={() => deactivate(a.machineId)}>
              {busy === a.machineId ? 'Removing…' : 'Deactivate'}
            </button>
          </dd>
        </div>
      ))}
      <div className="row">
        <dt>Seats used</dt>
        <dd>{activations.length} / {seats}</dd>
      </div>
    </dl>
  )
}

function Dashboard() {
  const { user, logout } = useAuth()
  const [licenses, setLicenses] = useState<License[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const call = httpsCallable<void, { licenses: License[] }>(requireFunctions(), 'myLicenses')
      const { data } = await call()
      setLicenses(data.licenses)
    } catch (e) {
      setError(errorMessage(e))
      setLicenses([])
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Dashboard n'est rendu que connecté ; la garde rassure le compilateur
  // sans introduire de chemin mort à l'exécution.
  if (!user) return null

  return (
    <>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
          <h2 style={{ margin: 0 }}>My licences</h2>
          <span className="badge">{user.email}</span>
        </div>

        {error && <p className="alert is-error">{error}</p>}
        {licenses === null && <p style={{ color: 'var(--muted)' }}>Loading…</p>}

        {licenses?.length === 0 && (
          <>
            <p style={{ color: 'var(--muted)' }}>
              No licence is attached to this address yet. If you bought with a different email,
              sign in with that one — licences are matched by the address used at checkout.
            </p>
            <BuyButton className="btn btn-primary btn-block" />
          </>
        )}

        {licenses?.map((l) => (
          <div key={l.key} style={{ marginTop: '1.2rem' }}>
            <p style={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '1.1rem', letterSpacing: '.08em', userSelect: 'all', margin: 0,
            }}>
              {l.key}
            </p>
            <Machines
              licenseKey={l.key}
              activations={l.activations}
              seats={l.seats}
              onChanged={load}
            />
          </div>
        ))}
      </div>

      <div style={{ height: '1rem' }} />

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Download</h3>
        <p style={{ color: 'var(--muted)' }}>The latest notarized build for macOS 15 and later.</p>
        <a className="btn btn-ghost btn-block" href={downloadURL}>Download AppSwitch</a>
      </div>

      <div style={{ height: '1.5rem' }} />
      <button className="btn btn-ghost btn-block" onClick={logout}>Sign out</button>
    </>
  )
}

export default function Account() {
  const { user, loading } = useAuth()

  if (!isFirebaseConfigured) {
    return (
      <main className="shell account">
        <p className="alert">
          Firebase n'est pas configuré. Copie <code>.env.example</code> en <code>.env.local</code>
          {' '}et renseigne les clés du projet.
        </p>
      </main>
    )
  }

  if (loading) return <main className="shell account"><p style={{ color: 'var(--muted)' }}>Loading…</p></main>

  return (
    <main className="shell account">
      {user ? <Dashboard /> : (
        <SignIn
          title="Find your licence"
          subtitle="Sign in with the email you used at checkout to see your key and manage your Macs."
        />
      )}
    </main>
  )
}
